import { n as u, t as init_esm } from "./esm-i_H2JYVp.js";
import { a as editor, i as Uri, o as languages, t as Emitter } from "./editor.api-BvHOuemv.js";
import { s as FindWidget } from "./wordPartOperations-BkZXvOXz.js";
import * as React from "react";
import { useCallback, useSyncExternalStore } from "react";
import { jsx } from "react/jsx-runtime";
//#region ../../node_modules/.pnpm/monaco-editor@0.57.0/node_modules/monaco-editor/esm/vs/features/find/register.js
var tabIndexPatchKey = "__monacoFindWidgetTabIndexPatchApplied";
var originalTabIndexKey = "__monacoFindWidgetOriginalTabIndex";
function setFindWidgetTabbableState(widget, visible) {
	const domNode = widget?._domNode;
	if (!domNode) return;
	const focusableElements = domNode.querySelectorAll("input, textarea, [tabindex], [role=\"button\"], [role=\"checkbox\"]");
	for (const element of focusableElements) {
		if (!(element instanceof HTMLElement)) continue;
		if (visible) {
			if (!(originalTabIndexKey in element.dataset)) continue;
			const originalTabIndex = element.dataset[originalTabIndexKey];
			if (originalTabIndex === "") element.removeAttribute("tabindex");
			else element.tabIndex = Number(originalTabIndex);
			delete element.dataset[originalTabIndexKey];
		} else {
			if (!(originalTabIndexKey in element.dataset)) element.dataset[originalTabIndexKey] = element.getAttribute("tabindex") ?? "";
			element.tabIndex = -1;
		}
	}
}
if (!FindWidget[tabIndexPatchKey]) {
	const originalReveal = FindWidget.prototype._reveal;
	const originalHide = FindWidget.prototype._hide;
	FindWidget.prototype._reveal = function(...args) {
		originalReveal.apply(this, args);
		setFindWidgetTabbableState(this, true);
	};
	FindWidget.prototype._hide = function(...args) {
		originalHide.apply(this, args);
		setFindWidgetTabbableState(this, false);
	};
	FindWidget[tabIndexPatchKey] = true;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-editor@0.57.0/node_modules/monaco-editor/esm/vs/languages/features/json/register.js
var LanguageServiceDefaultsImpl = class {
	constructor(languageId, diagnosticsOptions, modeConfiguration) {
		this._onDidChange = new Emitter();
		this._languageId = languageId;
		this.setDiagnosticsOptions(diagnosticsOptions);
		this.setModeConfiguration(modeConfiguration);
	}
	get onDidChange() {
		return this._onDidChange.event;
	}
	get languageId() {
		return this._languageId;
	}
	get modeConfiguration() {
		return this._modeConfiguration;
	}
	get diagnosticsOptions() {
		return this._diagnosticsOptions;
	}
	setDiagnosticsOptions(options) {
		this._diagnosticsOptions = options || /* @__PURE__ */ Object.create(null);
		this._onDidChange.fire(this);
	}
	setModeConfiguration(modeConfiguration) {
		this._modeConfiguration = modeConfiguration || /* @__PURE__ */ Object.create(null);
		this._onDidChange.fire(this);
	}
};
var jsonDefaults = new LanguageServiceDefaultsImpl("json", {
	validate: true,
	allowComments: true,
	schemas: [],
	enableSchemaRequest: false,
	schemaRequest: "warning",
	schemaValidation: "warning",
	comments: "error",
	trailingCommas: "error"
}, {
	documentFormattingEdits: true,
	documentRangeFormattingEdits: true,
	completionItems: true,
	hovers: true,
	documentSymbols: true,
	tokens: true,
	colors: true,
	foldingRanges: true,
	diagnostics: true,
	selectionRanges: true
});
function getMode() {
	return import("./jsonMode-BdYoa59x.js");
}
languages.register({
	id: "json",
	extensions: [
		".json",
		".bowerrc",
		".jshintrc",
		".jscsrc",
		".eslintrc",
		".babelrc",
		".har"
	],
	aliases: ["JSON", "json"],
	mimetypes: ["application/json"]
});
languages.onLanguage("json", () => {
	getMode().then((mode2) => mode2.setupMode(jsonDefaults));
});
//#endregion
//#region ../../node_modules/.pnpm/monaco-editor@0.57.0/node_modules/monaco-editor/esm/vs/languages/definitions/_.contribution.js
var languageDefinitions = {};
var lazyLanguageLoaders = {};
var LazyLanguageLoader = class LazyLanguageLoader {
	static getOrCreate(languageId) {
		if (!lazyLanguageLoaders[languageId]) lazyLanguageLoaders[languageId] = new LazyLanguageLoader(languageId);
		return lazyLanguageLoaders[languageId];
	}
	constructor(languageId) {
		this._languageId = languageId;
		this._loadingTriggered = false;
		this._lazyLoadPromise = new Promise((resolve, reject) => {
			this._lazyLoadPromiseResolve = resolve;
			this._lazyLoadPromiseReject = reject;
		});
	}
	load() {
		if (!this._loadingTriggered) {
			this._loadingTriggered = true;
			languageDefinitions[this._languageId].loader().then((mod) => this._lazyLoadPromiseResolve(mod), (err) => this._lazyLoadPromiseReject(err));
		}
		return this._lazyLoadPromise;
	}
};
function registerLanguage(def) {
	const languageId = def.id;
	languageDefinitions[languageId] = def;
	languages.register(def);
	const lazyLanguageLoader = LazyLanguageLoader.getOrCreate(languageId);
	languages.registerTokensProviderFactory(languageId, { create: async () => {
		return (await lazyLanguageLoader.load()).language;
	} });
	languages.onLanguageEncountered(languageId, async () => {
		const mod = await lazyLanguageLoader.load();
		languages.setLanguageConfiguration(languageId, mod.conf);
	});
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-editor@0.57.0/node_modules/monaco-editor/esm/vs/languages/definitions/yaml/register.js
registerLanguage({
	id: "yaml",
	extensions: [".yaml", ".yml"],
	aliases: [
		"YAML",
		"yaml",
		"YML",
		"yml"
	],
	mimetypes: ["application/x-yaml", "text/x-yaml"],
	loader: () => import("./yaml-aX3ywxSB.js")
});
//#endregion
//#region src/hooks/useResolvedColorMode.ts
var DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)";
function normalizeColorMode(colorMode) {
	return colorMode === "light" || colorMode === "dark" || colorMode === "system" ? colorMode : "system";
}
function getMediaQueryList() {
	if (typeof window !== "undefined" && typeof window.matchMedia === "function") return window.matchMedia(DARK_MEDIA_QUERY);
	return null;
}
function getSystemColorMode() {
	return getMediaQueryList()?.matches ? "dark" : "light";
}
function getServerColorMode() {
	return "light";
}
function noopUnsubscribe() {}
function useResolvedColorMode(colorMode) {
	const normalized = normalizeColorMode(colorMode);
	const subscribe = useCallback((onStoreChanges) => {
		if (normalized !== "system") return noopUnsubscribe;
		const mediaQuery = getMediaQueryList();
		if (mediaQuery == null) return noopUnsubscribe;
		mediaQuery.addEventListener("change", onStoreChanges);
		return () => {
			mediaQuery.removeEventListener("change", onStoreChanges);
		};
	}, [normalized]);
	return useSyncExternalStore(subscribe, () => normalized === "system" ? getSystemColorMode() : normalized, () => normalized === "system" ? getServerColorMode() : normalized);
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/marker-severity.js
/**
* Convert an LSP diagnostic severity to a Monaco editor marker severity.
*
* @param severity
*   The LSP diagnostic severity to convert.
* @returns
*   The diagnostic severity as Monaco editor marker severity.
*/
function toMarkerSeverity(severity) {
	if (severity === 4) return 1;
	if (severity === 3) return 2;
	if (severity === 2) return 4;
	return 8;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/marker-tag.js
/**
* Convert an LSP diagnostic tag to a Monaco editor marker tag.
*
* @param tag
*   The LSP diagnostic tag to convert.
* @returns
*   The diagnostic tag as Monaco editor marker tag.
*/
function toMarkerTag(tag) {
	return tag;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/range.js
/**
* Convert a Monaco editor range to an LSP range.
*
* @param range
*   The Monaco range to convert.
* @returns
*   The range as an LSP range.
*/
function fromRange(range) {
	return {
		start: {
			line: range.startLineNumber - 1,
			character: range.startColumn - 1
		},
		end: {
			line: range.endLineNumber - 1,
			character: range.endColumn - 1
		}
	};
}
/**
* Convert an LSP range to a Monaco editor range.
*
* @param range
*   The LSP range to convert.
* @returns
*   The range as Monaco editor range.
*/
function toRange(range) {
	return {
		startLineNumber: range.start.line + 1,
		startColumn: range.start.character + 1,
		endLineNumber: range.end.line + 1,
		endColumn: range.end.character + 1
	};
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/related-information.js
init_esm();
/**
* Convert an LSP diagnostic related information to a Monaco editor related information.
*
* @param relatedInformation
*   The LSP diagnostic related information to convert.
* @returns
*   The diagnostic related information as Monaco editor related information.
*/
function toRelatedInformation(relatedInformation) {
	return {
		...toRange(relatedInformation.location.range),
		message: relatedInformation.message,
		resource: u.parse(relatedInformation.location.uri)
	};
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/marker-data.js
init_esm();
/**
* Convert an LSP diagnostic to a Monaco editor marker data.
*
* @param diagnostic
*   The LSP diagnostic to convert.
* @returns
*   The diagnostic as Monaco editor marker data.
*/
function toMarkerData(diagnostic) {
	const markerData = {
		...toRange(diagnostic.range),
		message: diagnostic.message,
		severity: diagnostic.severity ? toMarkerSeverity(diagnostic.severity) : 8
	};
	if (diagnostic.code != null) markerData.code = diagnostic.codeDescription == null ? String(diagnostic.code) : {
		value: String(diagnostic.code),
		target: u.parse(diagnostic.codeDescription.href)
	};
	if (diagnostic.relatedInformation) markerData.relatedInformation = diagnostic.relatedInformation.map(toRelatedInformation);
	if (diagnostic.tags) markerData.tags = diagnostic.tags.map(toMarkerTag);
	if (diagnostic.source != null) markerData.source = diagnostic.source;
	return markerData;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/text-edit.js
/**
* Convert an LSP text edit to a Monaco editor text edit.
*
* @param textEdit
*   The LSP text edit to convert.
* @returns
*   The text edit as Monaco editor text edit.
*/
function toTextEdit(textEdit) {
	return {
		range: toRange(textEdit.range),
		text: textEdit.newText
	};
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/workspace-edit-metadata.js
/**
* Convert an LSP change annotation to a Monaco editor workspace edit metadata.
*
* @param changeAnnotation
*   The LSP workspace edit to convert.
* @returns
*   The change annotation as Monaco editor workspace edit metadata.
*/
function toWorkspaceEditMetadata(changeAnnotation) {
	var _a;
	const workspaceEditMetadata = {
		label: changeAnnotation.label,
		needsConfirmation: (_a = changeAnnotation.needsConfirmation) !== null && _a !== void 0 ? _a : false
	};
	if (changeAnnotation.description != null) workspaceEditMetadata.description = changeAnnotation.description;
	return workspaceEditMetadata;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/workspace-file-edit-options.js
/**
* Convert LSP workspace file edit options to Monaco editor workspace file edit options.
*
* @param options
*   The LSP workspace file edit options to convert.
* @returns
*   The workspace file edit options Monaco editor workspace file edit options.
*/
function toWorkspaceFileEditOptions(options) {
	const result = {};
	if (options.ignoreIfExists != null) result.ignoreIfExists = options.ignoreIfExists;
	if (options.ignoreIfNotExists != null) result.ignoreIfNotExists = options.ignoreIfNotExists;
	if (options.overwrite != null) result.overwrite = options.overwrite;
	if (options.recursive != null) result.recursive = options.recursive;
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/workspace-file-edit.js
init_esm();
/**
* Convert an LSP workspace file edit to a Monaco editor workspace file edit.
*
* @param workspaceFileEdit
*   The LSP workspace file edit to convert.
* @returns
*   The workspace file edit options Monaco editor workspace file edit options.
*/
function toWorkspaceFileEdit(workspaceFileEdit) {
	const result = workspaceFileEdit.kind === "create" ? { newResource: u.parse(workspaceFileEdit.uri) } : workspaceFileEdit.kind === "delete" ? { oldResource: u.parse(workspaceFileEdit.uri) } : {
		oldResource: u.parse(workspaceFileEdit.oldUri),
		newResource: u.parse(workspaceFileEdit.newUri)
	};
	if (workspaceFileEdit.options) result.options = toWorkspaceFileEditOptions(workspaceFileEdit.options);
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/workspace-edit.js
init_esm();
/**
* Convert an LSP text edit and uri to a Monaco editor workspace text edit.
*
* @param textEdit
*   The LSP text edit to convert.
* @param uri
*   The uri of the workspace text edit.
* @param changeAnnotations
*   The change annotations that are present in the workspace edit.
* @param versionId
*   The version ID of the workspace text edit.
* @returns
*   The text edit and uri as Monaco editor workspace text edit.
*/
function toWorkspaceTextEdit(textEdit, uri, changeAnnotations, versionId) {
	const workspaceTextEdit = {
		resource: u.parse(uri),
		versionId,
		textEdit: toTextEdit(textEdit)
	};
	if ("annotationId" in textEdit) {
		const changeAnnotation = changeAnnotations === null || changeAnnotations === void 0 ? void 0 : changeAnnotations[textEdit.annotationId];
		if (changeAnnotation) workspaceTextEdit.metadata = toWorkspaceEditMetadata(changeAnnotation);
	}
	return workspaceTextEdit;
}
/**
* Convert an LSP workspace edit to a Monaco editor workspace edit.
*
* @param workspaceEdit
*   The LSP workspace edit to convert.
* @returns
*   The workspace edit as Monaco editor workspace edit.
*/
function toWorkspaceEdit(workspaceEdit) {
	var _a;
	const edits = [];
	if (workspaceEdit.changes) for (const [uri, textEdits] of Object.entries(workspaceEdit.changes)) for (const textEdit of textEdits) edits.push(toWorkspaceTextEdit(textEdit, uri, workspaceEdit.changeAnnotations));
	if (workspaceEdit.documentChanges) for (const documentChange of workspaceEdit.documentChanges) {
		if (!("textDocument" in documentChange)) {
			edits.push(toWorkspaceFileEdit(documentChange));
			continue;
		}
		for (const textEdit of documentChange.edits) edits.push(toWorkspaceTextEdit(textEdit, documentChange.textDocument.uri, workspaceEdit.changeAnnotations, (_a = documentChange.textDocument.version) !== null && _a !== void 0 ? _a : void 0));
	}
	return { edits };
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/code-action.js
/**
* Convert an LSP code action to a Monaco editor code action.
*
* @param codeAction
*   The LSP code action to convert.
* @returns
*   The code action as Monaco editor code action.
*/
function toCodeAction(codeAction) {
	const result = {
		title: codeAction.title,
		isPreferred: codeAction.isPreferred
	};
	if (codeAction.diagnostics) result.diagnostics = codeAction.diagnostics.map(toMarkerData);
	if (codeAction.disabled) result.disabled = codeAction.disabled.reason;
	if (codeAction.edit) result.edit = toWorkspaceEdit(codeAction.edit);
	if (codeAction.isPreferred != null) result.isPreferred = codeAction.isPreferred;
	if (codeAction.kind) result.kind = codeAction.kind;
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/command.js
/**
* Convert an LSP command to a Monaco editor command.
*
* @param command
*   The LSP command to convert.
* @returns
*   The command as Monaco editor command.
*/
function toCommand(command) {
	const result = {
		title: command.title,
		id: command.command
	};
	if (command.arguments) result.arguments = command.arguments;
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/code-lens.js
/**
* Convert an LSP code lens to a Monaco editor code lens.
*
* @param codeLens
*   The LSP code lens to convert.
* @returns
*   The code lens as Monaco editor code lens.
*/
function toCodeLens(codeLens) {
	const result = { range: toRange(codeLens.range) };
	if (codeLens.command) result.command = toCommand(codeLens.command);
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/color.js
/**
* Convert an LSP color to a Monaco editor color.
*
* @param color
*   The LSP color to convert.
* @returns
*   The color as Monaco editor color.
*/
function toColor(color) {
	return {
		red: color.red,
		blue: color.blue,
		green: color.green,
		alpha: color.alpha
	};
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/color-information.js
/**
* Convert an LSP color information to a Monaco editor color information.
*
* @param colorInformation
*   The LSP color information to convert.
* @returns
*   The color information as Monaco editor color information.
*/
function toColorInformation(colorInformation) {
	return {
		range: toRange(colorInformation.range),
		color: toColor(colorInformation.color)
	};
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/color-presentation.js
/**
* Convert an LSP color presentation to a Monaco editor color presentation.
*
* @param colorPresentation
*   The LSP color presentation to convert.
* @returns
*   The color presentation as Monaco editor color presentation.
*/
function toColorPresentation(colorPresentation) {
	const result = { label: colorPresentation.label };
	if (colorPresentation.textEdit) result.textEdit = toTextEdit(colorPresentation.textEdit);
	if (colorPresentation.additionalTextEdits) result.additionalTextEdits = colorPresentation.additionalTextEdits.map(toTextEdit);
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/completion-trigger-kind.js
/**
* Convert a Monaco editor completion trigger kind to an LSP completion trigger kind.
*
* @param kind
*   The Monaco completion trigger kind to convert.
* @returns
*   The completion trigger kind as an LSP completion trigger kind.
*/
function fromCompletionTriggerKind(kind) {
	if (kind === 0) return 1;
	if (kind === 1) return 2;
	return 3;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/completion-context.js
/**
* Convert a Monaco editor completion context to an LSP completion context.
*
* @param completionContext
*   The Monaco completion context to convert.
* @returns
*   The completion context as an LSP completion context.
*/
function fromCompletionContext(completionContext) {
	const result = { triggerKind: fromCompletionTriggerKind(completionContext.triggerKind) };
	if (completionContext.triggerCharacter != null) result.triggerCharacter = completionContext.triggerCharacter;
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/completion-item-kind.js
/**
* Convert an LSP completion item kind to a Monaco editor completion item kind.
*
* @param kind
*   The LSP completion item kind to convert.
* @returns
*   The completion item kind as Monaco editor completion item kind.
*/
function toCompletionItemKind(kind) {
	if (kind === 1) return 18;
	if (kind === 2) return 0;
	if (kind === 3) return 1;
	if (kind === 4) return 2;
	if (kind === 5) return 3;
	if (kind === 6) return 4;
	if (kind === 7) return 5;
	if (kind === 8) return 7;
	if (kind === 9) return 8;
	if (kind === 10) return 9;
	if (kind === 11) return 12;
	if (kind === 12) return 13;
	if (kind === 13) return 15;
	if (kind === 14) return 17;
	if (kind === 15) return 27;
	if (kind === 16) return 19;
	if (kind === 17) return 20;
	if (kind === 18) return 21;
	if (kind === 19) return 23;
	if (kind === 20) return 16;
	if (kind === 21) return 14;
	if (kind === 22) return 6;
	if (kind === 23) return 10;
	if (kind === 24) return 11;
	return 24;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/completion-item-tag.js
/**
* Convert an LSP completion item tag to a Monaco editor completion item tag.
*
* @param tag
*   The LSP completion item tag to convert.
* @returns
*   The completion item tag as Monaco editor completion item tag.
*/
function toCompletionItemTag(tag) {
	return tag;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/markdown-string.js
/**
* Convert a Monaco editor markdown string to an LSP markup content.
*
* @param markdownString
*   The Monaco markdown string to convert.
* @returns
*   The markdown string as an LSP markup content.
*/
function fromMarkdownString(markdownString) {
	return {
		kind: "markdown",
		value: markdownString.value
	};
}
/**
* Convert an LSP markup content to a Monaco editor markdown string.
*
* @param markupContent
*   The LSP markup content to convert.
* @returns
*   The markup content as a Monaco editor markdown string.
*/
function toMarkdownString(markupContent) {
	return { value: markupContent.value };
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/single-edit-operation.js
/**
* Convert an LSP text edit to a Monaco editor single edit operation.
*
* @param textEdit
*   The LSP text edit to convert.
* @returns
*   The text edit as Monaco editor single edit operation.
*/
function toSingleEditOperation(textEdit) {
	return {
		range: toRange(textEdit.range),
		text: textEdit.newText
	};
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/completion-item.js
/**
* Convert an LSP completion item text edit to a Monaco editor range.
*
* @param edit
*   The LSP completion item text edit to convert.
* @returns
*   The completion item text edit as Monaco editor range.
*/
function toCompletionItemRange(edit) {
	if ("range" in edit) return toRange(edit.range);
	if ("insert" in edit && "replace" in edit) return {
		insert: toRange(edit.insert),
		replace: toRange(edit.replace)
	};
	return toRange(edit);
}
/**
* Convert an LSP completion item to a Monaco editor completion item.
*
* @param completionItem
*   The LSP completion item to convert.
* @param options
*   Additional options needed to construct the Monaco completion item.
* @returns
*   The completion item as Monaco editor completion item.
*/
function toCompletionItem(completionItem, options) {
	var _a, _b, _c, _d, _e;
	const itemDefaults = (_a = options.itemDefaults) !== null && _a !== void 0 ? _a : {};
	const textEdit = (_b = completionItem.textEdit) !== null && _b !== void 0 ? _b : itemDefaults.editRange;
	const commitCharacters = (_c = completionItem.commitCharacters) !== null && _c !== void 0 ? _c : itemDefaults.commitCharacters;
	const insertTextFormat = (_d = completionItem.insertTextFormat) !== null && _d !== void 0 ? _d : itemDefaults.insertTextFormat;
	const insertTextMode = (_e = completionItem.insertTextMode) !== null && _e !== void 0 ? _e : itemDefaults.insertTextMode;
	let text = completionItem.insertText;
	let range;
	if (textEdit) {
		range = toCompletionItemRange(textEdit);
		if ("newText" in textEdit) text = textEdit.newText;
	} else range = { ...options.range };
	const result = {
		insertText: text !== null && text !== void 0 ? text : completionItem.label,
		kind: completionItem.kind == null ? 18 : toCompletionItemKind(completionItem.kind),
		label: completionItem.label,
		range
	};
	if (completionItem.additionalTextEdits) result.additionalTextEdits = completionItem.additionalTextEdits.map(toSingleEditOperation);
	if (completionItem.command) result.command = toCommand(completionItem.command);
	if (commitCharacters) result.commitCharacters = commitCharacters;
	if (completionItem.detail != null) result.detail = completionItem.detail;
	if (typeof completionItem.documentation === "string") result.documentation = completionItem.documentation;
	else if (completionItem.documentation) result.documentation = toMarkdownString(completionItem.documentation);
	if (completionItem.filterText != null) result.filterText = completionItem.filterText;
	if (insertTextFormat === 2) result.insertTextRules = 4;
	else if (insertTextMode === 2) result.insertTextRules = 1;
	if (completionItem.preselect != null) result.preselect = completionItem.preselect;
	if (completionItem.sortText != null) result.sortText = completionItem.sortText;
	if (completionItem.tags) result.tags = completionItem.tags.map(toCompletionItemTag);
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/completion-list.js
/**
* Convert an LSP completion list to a Monaco editor completion list.
*
* @param completionList
*   The LSP completion list to convert.
* @param options
*   Additional options needed to construct the Monaco completion list.
* @returns
*   The completion list as Monaco editor completion list.
*/
function toCompletionList(completionList, options) {
	return {
		incomplete: Boolean(completionList.isIncomplete),
		suggestions: completionList.items.map((item) => toCompletionItem(item, {
			range: options.range,
			itemDefaults: completionList.itemDefaults
		}))
	};
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/location.js
init_esm();
/**
* Convert an LSP location to a Monaco editor location.
*
* @param location
*   The LSP location to convert.
* @returns
*   The location as Monaco editor location.
*/
function toLocation(location) {
	return {
		range: toRange(location.range),
		uri: u.parse(location.uri)
	};
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/document-highlight-kind.js
/**
* Convert an LSP document highlight kind to a Monaco editor document highlight kind.
*
* @param kind
*   The LSP document highlight kind to convert.
* @returns
*   The document highlight kind as Monaco editor document highlight kind.
*/
function toDocumentHighlightKind(kind) {
	if (kind === 2) return 1;
	if (kind === 3) return 2;
	return 0;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/document-highlight.js
/**
* Convert an LSP document highlight to a Monaco editor document highlight.
*
* @param documentHighlight
*   The LSP document highlight to convert.
* @returns
*   The document highlight as Monaco editor document highlight.
*/
function toDocumentHighlight(documentHighlight) {
	const result = { range: toRange(documentHighlight.range) };
	if (documentHighlight.kind != null) result.kind = toDocumentHighlightKind(documentHighlight.kind);
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/symbol-kind.js
/**
* Convert an LSP symbol kind to a Monaco editor symbol kind.
*
* @param symbolKind
*   The LSP symbol kind to convert.
* @returns
*   The symbol kind as Monaco editor symbol kind.
*/
function toSymbolKind(symbolKind) {
	if (symbolKind === 1) return 0;
	if (symbolKind === 2) return 1;
	if (symbolKind === 3) return 2;
	if (symbolKind === 4) return 3;
	if (symbolKind === 5) return 4;
	if (symbolKind === 6) return 5;
	if (symbolKind === 7) return 6;
	if (symbolKind === 8) return 7;
	if (symbolKind === 9) return 8;
	if (symbolKind === 10) return 9;
	if (symbolKind === 11) return 10;
	if (symbolKind === 12) return 11;
	if (symbolKind === 13) return 12;
	if (symbolKind === 14) return 13;
	if (symbolKind === 15) return 14;
	if (symbolKind === 16) return 15;
	if (symbolKind === 17) return 16;
	if (symbolKind === 18) return 17;
	if (symbolKind === 19) return 18;
	if (symbolKind === 20) return 19;
	if (symbolKind === 21) return 20;
	if (symbolKind === 22) return 21;
	if (symbolKind === 23) return 22;
	if (symbolKind === 24) return 23;
	if (symbolKind === 25) return 24;
	return 25;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/symbol-tag.js
/**
* Convert an LSP symbol tag to a Monaco editor symbol tag.
*
* @param symbolTag
*   The LSP symbol tag to convert.
* @returns
*   The symbol tag as Monaco editor symbol tag.
*/
function toSymbolTag(symbolTag) {
	return symbolTag;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/document-symbol.js
/**
* Convert an LSP document symbol to a Monaco editor document symbol.
*
* @param documentSymbol
*   The LSP document symbol to convert.
* @returns
*   The document symbol as Monaco editor document symbol.
*/
function toDocumentSymbol(documentSymbol) {
	var _a, _b, _c;
	const result = {
		detail: (_a = documentSymbol.detail) !== null && _a !== void 0 ? _a : "",
		kind: toSymbolKind(documentSymbol.kind),
		name: documentSymbol.name,
		range: toRange(documentSymbol.range),
		selectionRange: toRange(documentSymbol.selectionRange),
		tags: (_c = (_b = documentSymbol.tags) === null || _b === void 0 ? void 0 : _b.map(toSymbolTag)) !== null && _c !== void 0 ? _c : []
	};
	if (documentSymbol.children) result.children = documentSymbol.children.map(toDocumentSymbol);
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/folding-range.js
/**
* Convert an LSP folding range to a Monaco editor folding range.
*
* @param foldingRange
*   The LSP folding range to convert.
* @returns
*   The folding range as Monaco editor folding range.
*/
function toFoldingRange(foldingRange) {
	const result = {
		start: foldingRange.startLine + 1,
		end: foldingRange.endLine + 1
	};
	if (foldingRange.kind != null) result.kind = { value: foldingRange.kind };
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/formatting-options.js
/**
* Convert a Monaco editor formatting options to an LSP formatting options.
*
* @param formattingOptions
*   The Monaco formatting options to convert.
* @returns
*   The formatting options as an LSP formatting options.
*/
function fromFormattingOptions(formattingOptions) {
	return {
		insertSpaces: formattingOptions.insertSpaces,
		tabSize: formattingOptions.tabSize
	};
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/hover.js
/**
* Get value of a marked string.
*
* @param value
*   The marked string to get the value from.
* @returns
*   The value of the marked string.
*/
function getDeprecatedMarkupValue(value) {
	if (typeof value === "string") return { value };
	return { value: `\`\`\`${value.language}\n${value.value}\n\`\`\`` };
}
/**
* Convert LSP hover item contents to a Monaco markdown string.
*
* @param contents
*   The LSP hover contents to convert.
* @returns
*   The hover contents as a Monaco markdown string.
*/
function toHoverContents(contents) {
	if (typeof contents === "string" || "language" in contents) return [getDeprecatedMarkupValue(contents)];
	if (Array.isArray(contents)) return contents.map(getDeprecatedMarkupValue);
	return [toMarkdownString(contents)];
}
/**
* Convert an LSP hover to a Monaco editor hover.
*
* @param hover
*   The LSP hover to convert.
* @returns
*   The hover as Monaco editor hover.
*/
function toHover(hover) {
	const result = { contents: toHoverContents(hover.contents) };
	if (hover.range) result.range = toRange(hover.range);
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/inlay-hint-kind.js
/**
* Convert an LSP inlay hint kind to a Monaco editor inlay hint kind.
*
* @param inlayHintKind
*   The LSP inlay hint kind to convert.
* @returns
*   The inlay hint kind as Monaco editor inlay hint kind.
*/
function toInlayHintKind(inlayHintKind) {
	return inlayHintKind;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/inlay-hint-label-part.js
/**
* Convert an LSP inlay hint label part to a Monaco editor inlay hint label part.
*
* @param inlayHintLabelPart
*   The LSP inlay hint label part to convert.
* @returns
*   The inlay hint label part as Monaco editor inlay hint label part.
*/
function toInlayHintLabelPart(inlayHintLabelPart) {
	const result = { label: inlayHintLabelPart.value };
	if (inlayHintLabelPart.command) result.command = toCommand(inlayHintLabelPart.command);
	if (inlayHintLabelPart.location) result.location = toLocation(inlayHintLabelPart.location);
	if (typeof inlayHintLabelPart.tooltip === "string") result.tooltip = inlayHintLabelPart.tooltip;
	else if (inlayHintLabelPart.tooltip) result.tooltip = toMarkdownString(inlayHintLabelPart.tooltip);
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/position.js
/**
* Convert a Monaco editor position to an LSP range.
*
* @param position
*   The Monaco position to convert.
* @returns
*   The position as an LSP position.
*/
function fromPosition(position) {
	return {
		character: position.column - 1,
		line: position.lineNumber - 1
	};
}
/**
* Convert an LSP position to a Monaco editor position.
*
* @param position
*   The LSP position to convert.
* @returns
*   The position as Monaco editor position.
*/
function toPosition(position) {
	return {
		lineNumber: position.line + 1,
		column: position.character + 1
	};
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/inlay-hint.js
/**
* Convert an LSP inlay hint to a Monaco editor inlay hint.
*
* @param inlayHint
*   The LSP inlay hint to convert.
* @returns
*   The inlay hint as Monaco editor inlay hint.
*/
function toInlayHint(inlayHint) {
	const result = {
		label: typeof inlayHint.label === "string" ? inlayHint.label : inlayHint.label.map(toInlayHintLabelPart),
		position: toPosition(inlayHint.position)
	};
	if (inlayHint.kind != null) result.kind = toInlayHintKind(inlayHint.kind);
	if (inlayHint.paddingLeft != null) result.paddingLeft = inlayHint.paddingLeft;
	if (inlayHint.paddingRight != null) result.paddingRight = inlayHint.paddingRight;
	if (inlayHint.textEdits) result.textEdits = inlayHint.textEdits.map(toTextEdit);
	if (typeof inlayHint.tooltip === "string") result.tooltip = inlayHint.tooltip;
	else if (inlayHint.tooltip) result.tooltip = toMarkdownString(inlayHint.tooltip);
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/link.js
init_esm();
/**
* Convert an LSP document link to a Monaco editor link.
*
* @param documentLink
*   The LSP document link to convert.
* @returns
*   The document link as Monaco editor link.
*/
function toLink(documentLink) {
	const result = { range: toRange(documentLink.range) };
	if (documentLink.tooltip != null) result.tooltip = documentLink.tooltip;
	if (documentLink.target != null) result.url = u.parse(documentLink.target);
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/linked-editing-ranges.js
/**
* Convert LSP linked editing ranges to Monaco editor linked editing ranges.
*
* @param linkedEditingRanges
*   The LSP linked editing ranges to convert.
* @returns
*   The linked editing ranges Monaco editor linked editing ranges.
*/
function toLinkedEditingRanges(linkedEditingRanges) {
	const result = { ranges: linkedEditingRanges.ranges.map(toRange) };
	if (linkedEditingRanges.wordPattern != null) result.wordPattern = new RegExp(linkedEditingRanges.wordPattern);
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/location-link.js
init_esm();
/**
* Convert an LSP location link to a Monaco editor location link.
*
* @param locationLink
*   The LSP location link to convert.
* @returns
*   The location link as Monaco editor location link.
*/
function toLocationLink(locationLink) {
	const result = {
		range: toRange(locationLink.targetRange),
		targetSelectionRange: toRange(locationLink.targetSelectionRange),
		uri: u.parse(locationLink.targetUri)
	};
	if (locationLink.originSelectionRange) result.originSelectionRange = toRange(locationLink.originSelectionRange);
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/parameter-information.js
/**
* Convert a Monaco editor parameter information to an LSP parameter information.
*
* @param parameterInformation
*   The Monaco parameter information to convert.
* @returns
*   The parameter information as an LSP parameter information.
*/
function fromParameterInformation(parameterInformation) {
	const result = { label: parameterInformation.label };
	if (typeof parameterInformation.documentation === "string") result.documentation = parameterInformation.documentation;
	else if (parameterInformation.documentation) result.documentation = fromMarkdownString(parameterInformation.documentation);
	return result;
}
/**
* Convert an LSP parameter information to a Monaco editor parameter information.
*
* @param parameterInformation
*   The LSP parameter information to convert.
* @returns
*   The parameter information as Monaco editor parameter information.
*/
function toParameterInformation(parameterInformation) {
	const result = { label: parameterInformation.label };
	if (typeof parameterInformation.documentation === "string") result.documentation = parameterInformation.documentation;
	else if (parameterInformation.documentation) result.documentation = toMarkdownString(parameterInformation.documentation);
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/selection-ranges.js
/**
* Convert an LSP selection range to Monaco editor selection ranges.
*
* @param selectionRange
*   The LSP selection range to convert.
* @returns
*   The selection range as Monaco editor selection ranges.
*/
function toSelectionRanges(selectionRange) {
	const result = [];
	let current = selectionRange;
	while (current) {
		result.push({ range: toRange(current.range) });
		current = current.parent;
	}
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/semantic-tokens.js
/**
* Convert LSP semantic tokens to Monaco editor semantic tokens.
*
* @param semanticTokens
*   The LSP semantic tokens to convert.
* @returns
*   The semantic tokens as Monaco editor semantic tokens.
*/
function toSemanticTokens(semanticTokens) {
	const result = { data: Uint32Array.from(semanticTokens.data) };
	if (semanticTokens.resultId != null) result.resultId = semanticTokens.resultId;
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/signature-information.js
/**
* Convert a Monaco editor signature information to an LSP signature information.
*
* @param signatureInformation
*   The Monaco signature information to convert.
* @returns
*   The signature information as an LSP signature information.
*/
function fromSignatureInformation(signatureInformation) {
	const result = {
		label: signatureInformation.label,
		parameters: signatureInformation.parameters.map(fromParameterInformation)
	};
	if (typeof signatureInformation.documentation === "string") result.documentation = signatureInformation.documentation;
	else if (signatureInformation.documentation) result.documentation = fromMarkdownString(signatureInformation.documentation);
	if (signatureInformation.activeParameter != null) result.activeParameter = signatureInformation.activeParameter;
	return result;
}
/**
* Convert an LSP signature information to a Monaco editor signature information.
*
* @param signatureInformation
*   The LSP signature information to convert.
* @returns
*   The signature information as Monaco editor signature information.
*/
function toSignatureInformation(signatureInformation) {
	var _a, _b;
	const result = {
		label: signatureInformation.label,
		parameters: (_b = (_a = signatureInformation.parameters) === null || _a === void 0 ? void 0 : _a.map(toParameterInformation)) !== null && _b !== void 0 ? _b : []
	};
	if (typeof signatureInformation.documentation === "string") result.documentation = signatureInformation.documentation;
	else if (signatureInformation.documentation) result.documentation = toMarkdownString(signatureInformation.documentation);
	if (signatureInformation.activeParameter != null) result.activeParameter = signatureInformation.activeParameter;
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/signature-help.js
/**
* Convert a Monaco editor signature help to an LSP signature help.
*
* @param signatureHelp
*   The Monaco signature help to convert.
* @returns
*   The signature help as an LSP signature help.
*/
function fromSignatureHelp(signatureHelp) {
	return {
		activeParameter: signatureHelp.activeParameter,
		activeSignature: signatureHelp.activeSignature,
		signatures: signatureHelp.signatures.map(fromSignatureInformation)
	};
}
/**
* Convert an LSP signature help to a Monaco editor signature help.
*
* @param signatureHelp
*   The LSP signature help to convert.
* @returns
*   The signature help as Monaco editor signature help.
*/
function toSignatureHelp(signatureHelp) {
	var _a, _b;
	return {
		activeParameter: (_a = signatureHelp.activeParameter) !== null && _a !== void 0 ? _a : 0,
		activeSignature: (_b = signatureHelp.activeSignature) !== null && _b !== void 0 ? _b : 0,
		signatures: signatureHelp.signatures.map(toSignatureInformation)
	};
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/signature-help-trigger-kind.js
/**
* Convert a Monaco editor signature help trigger kind to an LSP signature help trigger kind.
*
* @param signatureHelpTriggerKind
*   The Monaco signature help trigger kind to convert.
* @returns
*   The signature help trigger kind as an LSP signature help trigger kind.
*/
function fromSignatureHelpTriggerKind(signatureHelpTriggerKind) {
	return signatureHelpTriggerKind;
}
//#endregion
//#region ../../node_modules/.pnpm/monaco-languageserver-types@0.4.1/node_modules/monaco-languageserver-types/dist/signature-help-context.js
/**
* Convert a Monaco editor signature help context to an LSP signature help context.
*
* @param signatureHelpContext
*   The Monaco signature help context to convert.
* @returns
*   The signature help context as an LSP signature help context.
*/
function fromSignatureHelpContext(signatureHelpContext) {
	const result = {
		isRetrigger: signatureHelpContext.isRetrigger,
		triggerKind: fromSignatureHelpTriggerKind(signatureHelpContext.triggerKind)
	};
	if (signatureHelpContext.triggerCharacter != null) result.triggerCharacter = signatureHelpContext.triggerCharacter;
	if (signatureHelpContext.activeSignatureHelp) result.activeSignatureHelp = fromSignatureHelp(signatureHelpContext.activeSignatureHelp);
	return result;
}
//#endregion
//#region ../../node_modules/.pnpm/@volar+monaco@2.4.28/node_modules/@volar/monaco/lib/markers.js
var markers = /* @__PURE__ */ new WeakMap();
//#endregion
//#region ../../node_modules/.pnpm/@volar+monaco@2.4.28/node_modules/@volar/monaco/lib/requestId.js
var requestIdMap = /* @__PURE__ */ new WeakMap();
function getRequestId(token, languageService) {
	const nextRequestId = (requestIdMap.get(languageService) ?? 0) + 1;
	requestIdMap.set(languageService, nextRequestId);
	token.onCancellationRequested(() => languageService.cancelRequest(nextRequestId));
	return nextRequestId;
}
//#endregion
//#region ../../node_modules/.pnpm/@volar+monaco@2.4.28/node_modules/@volar/monaco/lib/editor.js
function activateMarkers(worker, languages, markersOwn, getSyncUris, editor) {
	const disposables = [];
	const listener = /* @__PURE__ */ new Map();
	disposables.push(editor.onDidCreateModel((model) => hostingMarkers(model)), editor.onWillDisposeModel(stopHostingMarkers), editor.onDidChangeModelLanguage((event) => {
		stopHostingMarkers(event.model);
		hostingMarkers(event.model);
	}), { dispose: () => {
		for (const model of editor.getModels()) stopHostingMarkers(model);
	} });
	for (const model of editor.getModels()) hostingMarkers(model);
	return { dispose: () => disposables.forEach((d) => d.dispose()) };
	function stopHostingMarkers(model) {
		editor.setModelMarkers(model, markersOwn, []);
		const key = model.uri.toString();
		if (listener.has(key)) {
			listener.get(key)?.dispose();
			listener.delete(key);
		}
	}
	function hostingMarkers(model) {
		if (!languages.includes(model.getLanguageId?.() ?? model.getModeId?.())) return;
		let timer;
		const changeSubscription = model.onDidChangeContent(() => {
			clearTimeout(timer);
			timer = setTimeout(() => doValidation(model), 250);
		});
		const visibleSubscription = model.onDidChangeAttached(() => {
			if (model.isAttachedToEditor()) doValidation(model);
			else editor.setModelMarkers(model, markersOwn, []);
		});
		listener.set(model.uri.toString(), { dispose: () => {
			changeSubscription.dispose();
			visibleSubscription.dispose();
			clearTimeout(timer);
		} });
		doValidation(model);
	}
	async function doValidation(model) {
		if (model.isDisposed()) return;
		if (!model.isAttachedToEditor()) return;
		const languageService = await worker.withSyncedResources(getSyncUris());
		const token = createTokenForModelChange(model);
		const diagnostics = await languageService.getDiagnostics(getRequestId(token.token, languageService), model.uri);
		token.dispose();
		if (token.token.isCancellationRequested) return;
		const result = diagnostics.map((error) => {
			const marker = toMarkerData(error);
			markers.set(marker, error);
			return marker;
		});
		editor.setModelMarkers(model, markersOwn, result);
	}
}
function createTokenForModelChange(model) {
	const lastVersionId = model.getVersionId();
	let callbacks;
	return {
		token: {
			get isCancellationRequested() {
				return model.getVersionId() !== lastVersionId;
			},
			onCancellationRequested(cb) {
				const disposable = model.onDidChangeContent(() => {
					cb(void 0);
					disposable.dispose();
					callbacks?.delete(disposable);
				});
				callbacks ??= /* @__PURE__ */ new Set();
				callbacks.add(disposable);
				return { dispose() {
					disposable.dispose();
					callbacks?.delete(disposable);
				} };
			}
		},
		dispose() {
			if (callbacks) {
				for (const disposable of callbacks) disposable.dispose();
				callbacks.clear();
			}
		}
	};
}
//#endregion
//#region ../../node_modules/.pnpm/@volar+monaco@2.4.28/node_modules/@volar/monaco/lib/provider.js
async function createLanguageFeaturesProvider(worker, getSyncUris) {
	const completionItems = /* @__PURE__ */ new WeakMap();
	const codeLens = /* @__PURE__ */ new WeakMap();
	const codeActions = /* @__PURE__ */ new WeakMap();
	const colorInfos = /* @__PURE__ */ new WeakMap();
	const documentLinks = /* @__PURE__ */ new WeakMap();
	const inlayHints = /* @__PURE__ */ new WeakMap();
	const languageService = await worker.getProxy();
	const legend = await languageService.getSemanticTokenLegend();
	return {
		triggerCharacters: await languageService.getTriggerCharacters(),
		autoFormatTriggerCharacters: await languageService.getAutoFormatTriggerCharacters(),
		signatureHelpTriggerCharacters: await languageService.getSignatureHelpTriggerCharacters(),
		signatureHelpRetriggerCharacters: await languageService.getSignatureHelpRetriggerCharacters(),
		getLegend() {
			return legend;
		},
		async provideDocumentSemanticTokens(model, _lastResultId, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getSemanticTokens(getRequestId(token, languageService), model.uri, void 0, legend);
			if (codeResult) return toSemanticTokens(codeResult);
		},
		async provideDocumentRangeSemanticTokens(model, range, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getSemanticTokens(getRequestId(token, languageService), model.uri, fromRange(range), legend);
			if (codeResult) return toSemanticTokens(codeResult);
		},
		releaseDocumentSemanticTokens() {},
		async provideDocumentSymbols(model, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getDocumentSymbols(getRequestId(token, languageService), model.uri);
			if (codeResult) return codeResult.map(toDocumentSymbol);
		},
		async provideDocumentHighlights(model, position, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getDocumentHighlights(getRequestId(token, languageService), model.uri, fromPosition(position));
			if (codeResult) return codeResult.map(toDocumentHighlight);
		},
		async provideLinkedEditingRanges(model, position, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getLinkedEditingRanges(getRequestId(token, languageService), model.uri, fromPosition(position));
			if (codeResult) return toLinkedEditingRanges(codeResult);
		},
		async provideDefinition(model, position, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getDefinition(getRequestId(token, languageService), model.uri, fromPosition(position));
			if (codeResult) return codeResult.map(toLocationLink);
		},
		async provideImplementation(model, position, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getImplementations(getRequestId(token, languageService), model.uri, fromPosition(position));
			if (codeResult) return codeResult.map(toLocationLink);
		},
		async provideTypeDefinition(model, position, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getTypeDefinition(getRequestId(token, languageService), model.uri, fromPosition(position));
			if (codeResult) return codeResult.map(toLocationLink);
		},
		async provideCodeLenses(model, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getCodeLenses(getRequestId(token, languageService), model.uri);
			if (codeResult) {
				const monacoResult = codeResult.map(toCodeLens);
				for (let i = 0; i < monacoResult.length; i++) codeLens.set(monacoResult[i], codeResult[i]);
				return {
					lenses: monacoResult,
					dispose: () => {}
				};
			}
		},
		async resolveCodeLens(_, monacoResult, token) {
			let codeResult = codeLens.get(monacoResult);
			if (codeResult) {
				const languageService = await worker.withSyncedResources(getSyncUris());
				codeResult = await languageService.resolveCodeLens(getRequestId(token, languageService), codeResult);
				if (codeResult) {
					monacoResult = toCodeLens(codeResult);
					codeLens.set(monacoResult, codeResult);
				}
			}
			return monacoResult;
		},
		async provideCodeActions(model, range, context, token) {
			const diagnostics = [];
			for (const marker of context.markers) {
				const diagnostic = markers.get(marker);
				if (diagnostic) diagnostics.push(diagnostic);
			}
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getCodeActions(getRequestId(token, languageService), model.uri, fromRange(range), {
				diagnostics,
				only: context.only ? [context.only] : void 0
			});
			if (codeResult) {
				const monacoResult = codeResult.map(toCodeAction);
				for (let i = 0; i < monacoResult.length; i++) codeActions.set(monacoResult[i], codeResult[i]);
				return {
					actions: monacoResult,
					dispose: () => {}
				};
			}
		},
		async resolveCodeAction(monacoResult, token) {
			let codeResult = codeActions.get(monacoResult);
			if (codeResult) {
				const languageService = await worker.withSyncedResources(getSyncUris());
				codeResult = await languageService.resolveCodeAction(getRequestId(token, languageService), codeResult);
				if (codeResult) {
					monacoResult = toCodeAction(codeResult);
					codeActions.set(monacoResult, codeResult);
				}
			}
			return monacoResult;
		},
		async provideDocumentFormattingEdits(model, options, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getDocumentFormattingEdits(getRequestId(token, languageService), model.uri, fromFormattingOptions(options), void 0, void 0);
			if (codeResult) return codeResult.map(toTextEdit);
		},
		async provideDocumentRangeFormattingEdits(model, range, options, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getDocumentFormattingEdits(getRequestId(token, languageService), model.uri, fromFormattingOptions(options), fromRange(range), void 0);
			if (codeResult) return codeResult.map(toTextEdit);
		},
		async provideOnTypeFormattingEdits(model, position, ch, options, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getDocumentFormattingEdits(getRequestId(token, languageService), model.uri, fromFormattingOptions(options), void 0, {
				ch,
				position: fromPosition(position)
			});
			if (codeResult) return codeResult.map(toTextEdit);
		},
		async provideLinks(model, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getDocumentLinks(getRequestId(token, languageService), model.uri);
			if (codeResult) return { links: codeResult.map((link) => {
				const monacoLink = toLink(link);
				documentLinks.set(monacoLink, link);
				return monacoLink;
			}) };
		},
		async resolveLink(link, token) {
			let codeResult = documentLinks.get(link);
			if (codeResult) {
				codeResult = await languageService.resolveDocumentLink(getRequestId(token, languageService), codeResult);
				return toLink(codeResult);
			}
			return link;
		},
		async provideCompletionItems(model, position, context, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getCompletionItems(getRequestId(token, languageService), model.uri, fromPosition(position), fromCompletionContext(context));
			const monacoResult = toCompletionList(codeResult, { range: {
				startColumn: model.getWordUntilPosition(position).startColumn,
				startLineNumber: position.lineNumber,
				endColumn: position.column,
				endLineNumber: position.lineNumber
			} });
			for (let i = 0; i < codeResult.items.length; i++) completionItems.set(monacoResult.suggestions[i], codeResult.items[i]);
			return monacoResult;
		},
		async resolveCompletionItem(monacoItem, token) {
			let codeItem = completionItems.get(monacoItem);
			if (codeItem) {
				const languageService = await worker.withSyncedResources(getSyncUris());
				codeItem = await languageService.resolveCompletionItem(getRequestId(token, languageService), codeItem);
				monacoItem = toCompletionItem(codeItem, { range: "replace" in monacoItem.range ? monacoItem.range.replace : monacoItem.range });
				completionItems.set(monacoItem, codeItem);
			}
			return monacoItem;
		},
		async provideDocumentColors(model, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getDocumentColors(getRequestId(token, languageService), model.uri);
			if (codeResult) return codeResult.map(toColorInformation);
		},
		async provideColorPresentations(model, monacoResult, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = colorInfos.get(monacoResult);
			if (codeResult) {
				const codeColors = await languageService.getColorPresentations(getRequestId(token, languageService), model.uri, codeResult.color, {
					start: fromPosition(model.getPositionAt(0)),
					end: fromPosition(model.getPositionAt(model.getValueLength()))
				});
				if (codeColors) return codeColors.map(toColorPresentation);
			}
		},
		async provideFoldingRanges(model, _context, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getFoldingRanges(getRequestId(token, languageService), model.uri);
			if (codeResult) return codeResult.map(toFoldingRange);
		},
		async provideDeclaration(model, position, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getDefinition(getRequestId(token, languageService), model.uri, fromPosition(position));
			if (codeResult) return codeResult.map(toLocationLink);
		},
		async provideSelectionRanges(model, positions, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			return (await languageService.getSelectionRanges(getRequestId(token, languageService), model.uri, positions.map(fromPosition)))?.map(toSelectionRanges);
		},
		async provideSignatureHelp(model, position, token, context) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getSignatureHelp(getRequestId(token, languageService), model.uri, fromPosition(position), fromSignatureHelpContext(context));
			if (codeResult) return {
				value: toSignatureHelp(codeResult),
				dispose: () => {}
			};
		},
		async provideRenameEdits(model, position, newName, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getRenameEdits(getRequestId(token, languageService), model.uri, fromPosition(position), newName);
			if (codeResult) return toWorkspaceEdit(codeResult);
		},
		async provideReferences(model, position, context, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getReferences(getRequestId(token, languageService), model.uri, fromPosition(position), context);
			if (codeResult) return codeResult.map(toLocation);
		},
		async provideInlayHints(model, range, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getInlayHints(getRequestId(token, languageService), model.uri, fromRange(range));
			if (codeResult) return {
				hints: codeResult.map((hint) => {
					const monacoHint = toInlayHint(hint);
					inlayHints.set(monacoHint, hint);
					return monacoHint;
				}),
				dispose: () => {}
			};
		},
		async resolveInlayHint(hint, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeHint = inlayHints.get(hint);
			if (codeHint) return toInlayHint(await languageService.resolveInlayHint(getRequestId(token, languageService), codeHint));
			return hint;
		},
		async provideHover(model, position, token) {
			const languageService = await worker.withSyncedResources(getSyncUris());
			const codeResult = await languageService.getHover(getRequestId(token, languageService), model.uri, fromPosition(position));
			if (codeResult) return toHover(codeResult);
		}
	};
}
//#endregion
//#region ../../node_modules/.pnpm/@volar+monaco@2.4.28/node_modules/@volar/monaco/lib/languages.js
async function registerProviders(worker, language, getSyncUris, languages) {
	const provider = await createLanguageFeaturesProvider(worker, getSyncUris);
	const disposables = [
		languages.registerHoverProvider(language, provider),
		languages.registerReferenceProvider(language, provider),
		languages.registerRenameProvider(language, provider),
		languages.registerSignatureHelpProvider(language, provider),
		languages.registerDocumentSymbolProvider(language, provider),
		languages.registerDocumentHighlightProvider(language, provider),
		languages.registerLinkedEditingRangeProvider(language, provider),
		languages.registerDefinitionProvider(language, provider),
		languages.registerImplementationProvider(language, provider),
		languages.registerTypeDefinitionProvider(language, provider),
		languages.registerCodeLensProvider(language, provider),
		languages.registerCodeActionProvider(language, provider),
		languages.registerDocumentFormattingEditProvider(language, provider),
		languages.registerDocumentRangeFormattingEditProvider(language, provider),
		languages.registerOnTypeFormattingEditProvider(language, provider),
		languages.registerLinkProvider(language, provider),
		languages.registerCompletionItemProvider(language, provider),
		languages.registerColorProvider(language, provider),
		languages.registerFoldingRangeProvider(language, provider),
		languages.registerDeclarationProvider(language, provider),
		languages.registerSelectionRangeProvider(language, provider),
		languages.registerInlayHintsProvider(language, provider),
		languages.registerDocumentSemanticTokensProvider(language, provider),
		languages.registerDocumentRangeSemanticTokensProvider(language, provider)
	];
	return { dispose: () => disposables.forEach((d) => d.dispose()) };
}
//#endregion
//#region src/editor-commands.ts
function registerEditorCommands(model) {
	const insertHelloWorld = editor.registerCommand("openworkflow.insertHelloWorld", (_accessor, content) => {
		model.pushEditOperations(null, [{
			range: model.getFullModelRange(),
			text: content
		}], () => null);
	});
	return { dispose() {
		insertHelloWorld.dispose();
	} };
}
//#endregion
//#region src/language-service.ts
function createTextEditorLanguageService(model, createWorker) {
	const worker = editor.createWebWorker({ worker: Promise.resolve(createWorker()) });
	const commands = registerEditorCommands(model);
	const getSyncUris = () => model.getLanguageId() === "json" ? [model.uri] : [];
	const markers = activateMarkers(worker, ["json"], "openworkflow", getSyncUris, editor);
	let providers;
	let disposed = false;
	registerProviders(worker, "json", getSyncUris, languages).then((disposable) => {
		if (disposed) disposable.dispose();
		else providers = disposable;
	}).catch((error) => {
		if (!disposed) console.error("registerProviders failed", error);
	});
	return { dispose() {
		disposed = true;
		providers?.dispose();
		markers.dispose();
		commands.dispose();
		worker.dispose();
	} };
}
//#endregion
//#region src/TextEditor.tsx
jsonDefaults.setModeConfiguration({
	tokens: true,
	colors: false,
	completionItems: false,
	hovers: false,
	documentSymbols: false,
	documentFormattingEdits: false,
	documentRangeFormattingEdits: false,
	diagnostics: false,
	foldingRanges: false,
	selectionRanges: false
});
var TextEditor = ({ content, language, createLanguageServiceWorker, onContentChange, isReadOnly = false, colorMode = "system" }) => {
	const resolvedColorMode = useResolvedColorMode(colorMode);
	const containerRef = React.useRef(null);
	const editorRef = React.useRef(null);
	const isApplyingExternalContentRef = React.useRef(false);
	React.useEffect(() => {
		if (!containerRef.current) return;
		const model = editor.createModel(content, language, Uri.parse("inmemory://openworkflow/workflow.json"));
		const editor$2 = editor.create(containerRef.current, {
			model,
			readOnly: isReadOnly,
			codeLens: language === "json" && !isReadOnly,
			automaticLayout: true,
			renderLineHighlight: "none",
			...resolvedColorMode && { theme: resolvedColorMode === "dark" ? "vs-dark" : "vs" }
		});
		const languageService = createTextEditorLanguageService(model, createLanguageServiceWorker);
		editorRef.current = editor$2;
		return () => {
			languageService.dispose();
			editor$2.dispose();
			model.dispose();
			editorRef.current = null;
		};
	}, []);
	React.useEffect(() => {
		const editor = editorRef.current;
		if (!editor || !onContentChange) return;
		const disposable = editor.onDidChangeModelContent(() => {
			if (!isApplyingExternalContentRef.current) onContentChange(editor.getValue());
		});
		return () => disposable.dispose();
	}, [onContentChange]);
	React.useEffect(() => {
		const editor = editorRef.current;
		if (!editor || editor.getValue() === content) return;
		isApplyingExternalContentRef.current = true;
		editor.setValue(content);
		isApplyingExternalContentRef.current = false;
	}, [content]);
	React.useEffect(() => {
		const editor$1 = editorRef.current;
		if (!editor$1) return;
		editor$1.updateOptions({
			readOnly: isReadOnly,
			codeLens: language === "json" && !isReadOnly
		});
		const model = editor$1.getModel();
		if (model && model.getLanguageId() !== language) editor.setModelLanguage(model, language);
	}, [isReadOnly, language]);
	React.useEffect(() => {
		if (!editorRef.current) return;
		editor.setTheme(resolvedColorMode === "dark" ? "vs-dark" : "vs");
	}, [resolvedColorMode]);
	return /* @__PURE__ */ jsx("div", {
		"data-testid": "text-editor-container",
		ref: containerRef,
		style: {
			width: "100%",
			height: "100%"
		}
	});
};
//#endregion
export { TextEditor };

//# sourceMappingURL=index.js.map